"use client";

/**
 * SendConfirmationCard - what an email send will do, and the button that does it.
 *
 * Shown for a send tool's "pending_confirmation" result. Send now sends a user
 * message carrying metadata.confirmSend; only that authorises the send tool
 * (see lib/sola/confirm-send), so SOLA can never send an email on its own.
 */

import { useState } from "react";
import { Button } from "@/ui";
import { useQuickReply } from "../../QuickReplyContext";
import { confirmSendMessageText, type ConfirmSendKind } from "@/lib/sola/confirm-send";
import styles from "./SendConfirmationCard.module.css";

export interface PendingSend {
  status: "pending_confirmation";
  kind?: ConfirmSendKind;
  emailId: string;
  subject?: string;
  floatName?: string;
  recipients?: Array<{ id?: string | number; name?: string; email?: string }>;
  candidates?: string[];
}

export function isPendingSend(output: unknown): output is PendingSend {
  return !!output && typeof output === "object" && (output as { status?: unknown }).status === "pending_confirmation";
}

export function SendConfirmationCard({ pending }: { pending: PendingSend }) {
  const quickReply = useQuickReply();
  const [clicked, setClicked] = useState(false);
  const kind = pending.kind ?? "float";
  const recipients = pending.recipients ?? [];

  const send = () => {
    setClicked(true);
    quickReply?.sendMessage(confirmSendMessageText(kind, pending.emailId), {
      confirmSend: { kind, emailId: pending.emailId },
    });
  };

  return (
    <div className={styles.card} data-testid="send-confirmation">
      <div className={styles.header}>Ready to send {kind === "float" ? "float" : "submission"} email</div>
      <div className={styles.body}>
        {pending.subject && (
          <div className={styles.row}>
            <span className={styles.label}>Subject</span>
            <span>{pending.subject}</span>
          </div>
        )}
        <div className={styles.row}>
          <span className={styles.label}>To</span>
          <span>{recipients.map((r) => (r.name ? `${r.name}${r.email ? ` <${r.email}>` : ""}` : r.email)).join(", ") || "No recipients"}</span>
        </div>
        {!!pending.candidates?.length && (
          <div className={styles.row}>
            <span className={styles.label}>Candidates</span>
            <span>{pending.candidates.join(", ")}</span>
          </div>
        )}
      </div>
      <div className={styles.footer}>
        <Button variant="primary" size="sm" onClick={send} disabled={clicked || !quickReply || recipients.length === 0}>
          {clicked ? "Sending…" : "Send now"}
        </Button>
        <span className={styles.hint}>Nothing is sent until you click Send now.</span>
      </div>
    </div>
  );
}
